import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Badge from "@cloudscape-design/components/badge";
import Modal from "@cloudscape-design/components/modal";
import Alert from "@cloudscape-design/components/alert";
import Header from "@cloudscape-design/components/header";
import Link from "@cloudscape-design/components/link";
import Pagination from "@cloudscape-design/components/pagination";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import Tabs from "@cloudscape-design/components/tabs";
import DocumentDetails from "./DocumentDetails";
import CONFIG from "../config";

function DocumentList({ idToken, refreshTrigger, onLogout, currentUser, isAdmin, activeTabOverride, autoOpenDocumentId, onAutoOpenHandled }) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ visible: false, document: null, isBulk: false });
  const [processModal, setProcessModal] = useState({ visible: false, document: null, isBulk: false });
  const [detailsModal, setDetailsModal] = useState({ visible: false, document: null });
  const [alert, setAlert] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortingColumn, setSortingColumn] = useState({ sortingField: "upload_date" });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [activeTabId, setActiveTabId] = useState(activeTabOverride || "my-uploads");
  const [showAllTabs, setShowAllTabs] = useState(false);
  const [processingDocuments, setProcessingDocuments] = useState(new Set());

  // handleUnauthorized removed - 401/403 handled by global fetch interceptor in App.js

  // Clean up stale selections when documents change
  useEffect(() => {
    if (selectedItems.length > 0 && documents.length > 0) {
      const documentIds = new Set(documents.map((doc) => doc.id));
      const validSelections = selectedItems.filter((item) => documentIds.has(item.id));
      if (validSelections.length !== selectedItems.length) {
        setSelectedItems(validSelections);
      }
    }
  }, [documents, selectedItems]);

  const loadDocuments = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        const response = await fetch(`${CONFIG.apiUrl}documents`, {
          headers: { Authorization: idToken },
        });

        // 401/403 handled by global fetch interceptor in App.js

        if (response.ok) {
          const data = await response.json();
          setDocuments(data.documents);

          // Track documents that are currently processing
          const processing = new Set();
          data.documents.forEach((doc) => {
            if (doc.processing_status === "processing" || doc.processing_status === "pending") {
              processing.add(doc.id);
            }
          });
          setProcessingDocuments(processing);
        }
      } catch (error) {
        console.error("Failed to load documents:", error);
        // Network errors handled by global fetch interceptor
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [idToken],
  );

  const loadConfig = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}admin/config`, {
        headers: { Authorization: idToken },
      });

      if (response.ok) {
        const data = await response.json();
        setShowAllTabs(data.show_all_document_tabs || false);
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  }, [idToken]);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.apiUrl}document-types`, {
        headers: { Authorization: idToken },
      });

      // 401/403 handled by global fetch interceptor in App.js

      if (response.ok) {
        const data = await response.json();
        const typeMap = {};
        data.document_types.forEach((type) => {
          typeMap[type.id] = type.name;
        });
        setDocumentTypes(typeMap);
      }
    } catch (error) {
      console.error("Failed to load document types:", error);
    }
  }, [idToken]);

  useEffect(() => {
    loadDocuments();
    loadDocumentTypes();
    loadConfig();
  }, [refreshTrigger, loadDocuments, loadDocumentTypes, loadConfig]);

  // Auto-open document details when directed by parent (e.g. after processing completes)
  useEffect(() => {
    if (!autoOpenDocumentId || documents.length === 0) return;
    const doc = documents.find((d) => d.id === autoOpenDocumentId);
    if (doc) {
      setDetailsModal({ visible: true, document: doc });
      if (onAutoOpenHandled) onAutoOpenHandled();
    }
  }, [autoOpenDocumentId, documents, onAutoOpenHandled]);

  // Polling effect - poll every 3 seconds when there are processing documents
  useEffect(() => {
    if (processingDocuments.size === 0) return;

    console.log(`Starting polling for ${processingDocuments.size} processing document(s)`);

    const interval = setInterval(() => {
      console.log("Polling for document updates...");
      loadDocuments(true); // Silent reload (no loading spinner)
    }, 3000); // Poll every 3 seconds

    // Cleanup on unmount or when dependencies change
    return () => {
      console.log("Stopping polling");
      clearInterval(interval);
    };
  }, [processingDocuments.size, loadDocuments]);

  const handleProcess = async (document) => {
    // Close modal immediately
    setProcessModal({ visible: false, document: null, isBulk: false });

    // Add to processing set immediately to start polling
    setProcessingDocuments((prev) => new Set([...prev, document.id]));

    try {
      const response = await fetch(`${CONFIG.apiUrl}documents/${document.id}/process`, {
        method: "POST",
        headers: { Authorization: idToken },
      });

      // 401/403 handled by global fetch interceptor in App.js

      if (response.ok) {
        setAlert({ type: "success", message: t("venue.documents.list.processingStarted") });
        // Refresh to get updated status
        loadDocuments(true);
      } else {
        // Remove from processing set if request failed
        setProcessingDocuments((prev) => {
          const newSet = new Set(prev);
          newSet.delete(document.id);
          return newSet;
        });
        throw new Error(t("venue.documents.list.processingFailed"));
      }
    } catch (error) {
      setAlert({ type: "error", message: error.message });
      // Network errors handled by global fetch interceptor
      // Refresh on error too
      loadDocuments();
    }
  };

  const handleBulkProcess = async () => {
    // Close modal immediately
    setProcessModal({ visible: false, document: null, isBulk: false });

    // Add all selected documents to processing set immediately
    const docIds = selectedItems.map((doc) => doc.id);
    setProcessingDocuments((prev) => new Set([...prev, ...docIds]));

    let successCount = 0;
    let failCount = 0;
    const failedIds = [];

    try {
      for (const doc of selectedItems) {
        try {
          const response = await fetch(`${CONFIG.apiUrl}documents/${doc.id}/process`, {
            method: "POST",
            headers: { Authorization: idToken },
          });

          // 401/403 handled by global fetch interceptor in App.js

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
            failedIds.push(doc.id);
          }
        } catch (error) {
          failCount++;
          failedIds.push(doc.id);
        }
      }

      // Remove failed documents from processing set
      if (failedIds.length > 0) {
        setProcessingDocuments((prev) => {
          const newSet = new Set(prev);
          failedIds.forEach((id) => newSet.delete(id));
          return newSet;
        });
      }

      if (successCount > 0) {
        setAlert({ type: "success", message: `Processing started for ${successCount} document(s)` });
      }
      if (failCount > 0) {
        setAlert({ type: "error", message: `Failed to process ${failCount} document(s)` });
      }

      setSelectedItems([]);
      loadDocuments(true);
    } catch (error) {
      setAlert({ type: "error", message: error.message });
      setProcessModal({ visible: false, document: null, isBulk: false });
    }
  };

  const handleDelete = async () => {
    const docId = deleteModal.document.id;

    // Close modal immediately
    setDeleteModal({ visible: false, document: null, isBulk: false });

    try {
      const response = await fetch(`${CONFIG.apiUrl}documents/${docId}`, {
        method: "DELETE",
        headers: { Authorization: idToken },
      });

      // 401/403 handled by global fetch interceptor in App.js

      if (response.ok) {
        setAlert({ type: "success", message: t("venue.documents.list.deleteSuccess") });
        // Clear selection if deleted item was selected
        setSelectedItems((prev) => prev.filter((item) => item.id !== docId));
        loadDocuments();
      } else {
        throw new Error(t("venue.documents.list.deleteFailed"));
      }
    } catch (error) {
      setAlert({ type: "error", message: error.message });
      // Network errors handled by global fetch interceptor
      loadDocuments();
    }
  };

  const handleBulkDelete = async () => {
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    try {
      setDeleteModal({ visible: false, document: null, isBulk: false });

      for (const doc of selectedItems) {
        try {
          const response = await fetch(`${CONFIG.apiUrl}documents/${doc.id}`, {
            method: "DELETE",
            headers: { Authorization: idToken },
          });

          // 401/403 handled by global fetch interceptor in App.js

          if (response.ok) {
            successCount++;
          } else {
            const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
            console.error("Failed to delete document:", doc.file_name, errorData);
            errors.push(`${doc.file_name}: ${errorData.error || response.statusText}`);
            failCount++;
          }
        } catch (error) {
          console.error("Error deleting document:", doc.file_name, error);
          errors.push(`${doc.file_name}: ${error.message}`);
          failCount++;
        }

        // Small delay to avoid overwhelming the API
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (successCount > 0 && failCount === 0) {
        setAlert({ type: "success", message: `Successfully deleted ${successCount} document(s)` });
      } else if (successCount > 0 && failCount > 0) {
        setAlert({ type: "warning", message: `Deleted ${successCount} document(s), but ${failCount} failed. Check console for details.` });
      } else if (failCount > 0) {
        setAlert({ type: "error", message: `Failed to delete ${failCount} document(s). Check console for details.` });
      }

      setSelectedItems([]);
      loadDocuments();
    } catch (error) {
      console.error("Bulk delete error:", error);
      setAlert({ type: "error", message: error.message });
      setDeleteModal({ visible: false, document: null, isBulk: false });
    }
  };

  const getStatusIndicator = (status) => {
    const statusMap = {
      pending: { type: "pending", text: t("venue.documents.details.statusPending") },
      processing: { type: "in-progress", text: t("venue.documents.details.statusProcessing") },
      completed: { type: "success", text: t("venue.documents.details.statusCompleted") },
      failed: { type: "error", text: t("venue.documents.details.statusFailed") },
    };
    const config = statusMap[status] || { type: "info", text: status };
    return <StatusIndicator type={config.type}>{config.text}</StatusIndicator>;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleString();
  };

  const truncateFilename = (filename, maxLength = 25) => {
    if (filename.length <= maxLength) return filename;
    const ext = filename.split(".").pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
    const truncatedName = nameWithoutExt.substring(0, maxLength - ext.length - 4) + "...";
    return truncatedName + "." + ext;
  };

  // Filter documents based on active tab (use override if provided by parent)
  const effectiveTabId = activeTabOverride || activeTabId;
  const filteredDocuments = documents.filter((doc) => {
    if (effectiveTabId === "my-uploads") {
      return doc.uploaded_by_email === currentUser;
    } else if (effectiveTabId === "bulk-uploads") {
      return doc.upload_method === "s3_bucket";
    } else if (effectiveTabId === "other") {
      return doc.uploaded_by_email !== currentUser && doc.upload_method !== "s3_bucket";
    }
    return true;
  });

  // Sort documents
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    const field = sortingColumn.sortingField;
    let aVal = a[field];
    let bVal = b[field];

    // Handle null/undefined values
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // String comparison
    if (typeof aVal === "string") {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortingDescending ? -comparison : comparison;
  });

  // Determine which tabs to show
  const shouldShowAllTabs = isAdmin || showAllTabs;

  const tabs = [
    {
      id: "my-uploads",
      label: t("venue.documents.tabs.myUploads") || "My Uploads",
      content: null, // Will be filled below
    },
  ];

  if (shouldShowAllTabs) {
    tabs.push(
      {
        id: "bulk-uploads",
        label: t("venue.documents.tabs.bulkUploads") || "Bulk Uploads",
        content: null,
      },
      {
        id: "other",
        label: t("venue.documents.tabs.other") || "Other Documents",
        content: null,
      },
    );
  }

  const renderTable = () => (
    <Table
      columnDefinitions={[
        {
          id: "file_name",
          header: t("venue.documents.list.fileName"),
          cell: (item) => (
            <Link onFollow={() => setDetailsModal({ visible: true, document: item })} title={item.file_name}>
              {truncateFilename(item.file_name)}
            </Link>
          ),
          sortingField: "file_name",
        },
        {
          id: "document_type",
          header: t("venue.documents.upload.documentType"),
          cell: (item) => {
            if (item.document_type_id) {
              const typeName = documentTypes[item.document_type_id] || "Unknown";
              return (
                <SpaceBetween direction="horizontal" size="xs">
                  <Badge color="green">{typeName}</Badge>
                  {item.type_auto_detected && <Badge>{t("venue.common.auto")}</Badge>}
                </SpaceBetween>
              );
            }
            return <Badge color="grey">{t("venue.common.unspecified")}</Badge>;
          },
        },
        {
          id: "file_type",
          header: t("venue.documents.list.type"),
          cell: (item) => {
            const ext = item.file_name.split(".").pop().toUpperCase();
            return <Badge color="blue">{ext}</Badge>;
          },
        },
        {
          id: "file_size",
          header: t("venue.documents.list.size"),
          cell: (item) => formatFileSize(item.file_size),
        },
        {
          id: "uploaded_by",
          header: t("venue.documents.list.uploadedBy"),
          cell: (item) => item.uploaded_by_email || item.uploaded_by || "-",
        },
        {
          id: "upload_date",
          header: t("venue.documents.list.uploaded"),
          cell: (item) => formatDate(item.upload_date),
          sortingField: "upload_date",
        },
        {
          id: "processing_status",
          header: t("venue.documents.list.status"),
          cell: (item) => getStatusIndicator(item.processing_status),
        },
      ]}
      items={sortedDocuments.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize)}
      loading={loading}
      loadingText={t("venue.documents.list.loadingDocuments")}
      selectionType="multi"
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      ariaLabels={{
        selectionGroupLabel: t("venue.documents.list.selectionGroupLabel", "Items selection"),
        itemSelectionLabel: ({ selectedItems }, item) => {
          const isItemSelected = selectedItems.filter((i) => i.id === item.id).length;
          return `${item.file_name} is ${isItemSelected ? "" : "not "}selected`;
        },
      }}
      selectedItemsCount={selectedItems.length > 0 ? selectedItems.length : undefined}
      trackBy="id"
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={({ detail }) => {
        setSortingColumn(detail.sortingColumn);
        setSortingDescending(detail.isDescending);
      }}
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={Math.ceil(sortedDocuments.length / pageSize)}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          ariaLabels={{
            nextPageLabel: "Next page",
            previousPageLabel: "Previous page",
            pageLabel: (pageNumber) => `Page ${pageNumber}`,
          }}
        />
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={{
            pageSize,
          }}
          pageSizePreference={{
            title: "Page size",
            options: [
              { value: 10, label: "10 documents" },
              { value: 20, label: "20 documents" },
              { value: 50, label: "50 documents" },
              { value: 100, label: "100 documents" },
            ],
          }}
          onConfirm={({ detail }) => {
            setPageSize(detail.pageSize);
            setCurrentPageIndex(1);
          }}
        />
      }
      header={
        <Header
          counter={`(${filteredDocuments.length})`}
          description={
            processingDocuments.size > 0 ? (
              <Box color="text-status-info">
                <StatusIndicator type="in-progress">
                  {t("venue.documents.list.autoRefreshing", "Auto-refreshing")} - {processingDocuments.size}{" "}
                  {t("venue.documents.list.documentsProcessing", "document(s) processing")}
                </StatusIndicator>
              </Box>
            ) : null
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {selectedItems.length > 0 && (
                <ButtonDropdown
                  items={[
                    {
                      id: "process",
                      text: t("venue.documents.bulk.processSelected"),
                      iconName: "refresh",
                      disabled: selectedItems.every((item) => item.processing_status === "processing"),
                    },
                    {
                      id: "delete",
                      text: t("venue.documents.bulk.deleteSelected"),
                      iconName: "remove",
                    },
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === "process") {
                      setProcessModal({ visible: true, document: null, isBulk: true });
                    } else if (detail.id === "delete") {
                      setDeleteModal({ visible: true, document: null, isBulk: true });
                    }
                  }}
                >
                  {t("venue.documents.list.bulkActions", "Actions")}
                </ButtonDropdown>
              )}
              <Button iconName="refresh" onClick={() => loadDocuments(false)}>
                {t("venue.documents.list.refresh")}
              </Button>
            </SpaceBetween>
          }
        >
          {t("venue.documents.list.title")}
        </Header>
      }
      empty={
        <Box textAlign="center" color="inherit">
          <b>{t("venue.documents.list.noDocuments")}</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            {t("venue.documents.list.noDocumentsDescription")}
          </Box>
        </Box>
      }
    />
  );

  return (
    <>
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {activeTabOverride ? (
        renderTable()
      ) : (
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => {
            setActiveTabId(detail.activeTabId);
            setCurrentPageIndex(1);
            setSelectedItems([]);
          }}
          tabs={tabs.map((tab) => ({
            ...tab,
            content: renderTable(),
          }))}
        />
      )}

      <Modal
        visible={deleteModal.visible}
        onDismiss={() => setDeleteModal({ visible: false, document: null, isBulk: false })}
        header={deleteModal.isBulk ? t("venue.documents.bulk.deleteMultipleTitle") : t("venue.documents.list.deleteModalTitle")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteModal({ visible: false, document: null, isBulk: false })}>
                {t("venue.common.cancel")}
              </Button>
              <Button variant="primary" onClick={deleteModal.isBulk ? handleBulkDelete : handleDelete} loading={loading}>
                {t("venue.common.delete")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {deleteModal.isBulk
          ? t("venue.documents.bulk.deleteMultipleMessage", { count: selectedItems.length })
          : t("venue.documents.list.deleteModalMessage", { fileName: deleteModal.document?.file_name })}
      </Modal>

      <Modal
        visible={processModal.visible}
        onDismiss={() => setProcessModal({ visible: false, document: null, isBulk: false })}
        header={processModal.isBulk ? t("venue.documents.bulk.processMultipleTitle") : t("venue.documents.list.processModalTitle")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setProcessModal({ visible: false, document: null, isBulk: false })}>
                {t("venue.common.cancel")}
              </Button>
              <Button variant="primary" onClick={processModal.isBulk ? handleBulkProcess : () => handleProcess(processModal.document)} loading={loading}>
                {t("venue.documents.list.startProcessing")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {processModal.isBulk
          ? t("venue.documents.bulk.processMultipleMessage", { count: selectedItems.length })
          : t("venue.documents.list.processModalMessage", { fileName: processModal.document?.file_name })}
      </Modal>

      <DocumentDetails
        visible={detailsModal.visible}
        document={detailsModal.document}
        onDismiss={() => setDetailsModal({ visible: false, document: null })}
        idToken={idToken}
      />
    </>
  );
}

export default DocumentList;
